import { useEffect, useState } from "react";
import { Gift, Check, Loader2 } from "lucide-react";
import { api, errText } from "../lib/api";
import { t } from "../i18n";

// After paying, a buyer can still add chocolates, a cake or a vase — until a florist accepts the order.
export const ExtrasUpsell = ({ locale, orderNumber, email, currency }) => {
  const [data, setData] = useState(null);
  const [picked, setPicked] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!orderNumber || !email) return;
    api.get(`/orders/${orderNumber}/extras`, { params: { email, locale, currency } })
      .then((r) => setData(r.data)).catch(() => {});
  }, [orderNumber, email, locale, currency]);

  if (!data?.can_add || !data.extras?.length) return null;
  const chosen = Object.entries(picked).filter(([, q]) => q > 0);
  const total = chosen.reduce((sum, [id, q]) => sum + q * (data.extras.find((e) => e.id === id)?.price || 0), 0);

  const pay = async () => {
    setBusy(true); setError("");
    try {
      const { data: res } = await api.post(`/orders/${orderNumber}/extras`, {
        email, locale, origin_url: window.location.origin,
        items: chosen.map(([addon_id, quantity]) => ({ addon_id, quantity })),
      });
      window.location.href = res.checkout_url;
    } catch (e) { setError(errText(e)); setBusy(false); }
  };

  return (
    <section className="card p-6 space-y-4" data-testid="extras-upsell">
      <div className="flex items-center gap-2">
        <Gift size={16} className="text-brass" />
        <h2 className="font-display text-xl text-ink">{t(locale, "addExtrasTitle")}</h2>
      </div>
      <p className="text-sm text-muted">
        {t(locale, "addExtrasHelp").replace("{who}", data.recipient_name || "")}
      </p>
      <div className="grid sm:grid-cols-2 gap-3">
        {data.extras.map((e) => {
          const qty = picked[e.id] || 0;
          return (
            <button key={e.id} type="button" data-testid={`upsell-${e.id}`}
                    onClick={() => setPicked((p) => ({ ...p, [e.id]: qty ? 0 : 1 }))}
                    className={`flex items-center gap-3 border p-3 text-start transition-colors duration-200 ${
                      qty ? "border-brass bg-petal/20" : "border-brass-light hover:border-brass"}`}>
              {e.image_url ? <img src={e.image_url} alt="" className="w-12 h-12 object-cover rounded-sm" /> : null}
              <span className="flex-1 text-sm text-ink">{e.name}</span>
              <span className="text-sm tabular text-muted">{data.currency === "MAD" ? `${e.price_mad} MAD` : `${e.price} ${data.currency}`}</span>
              {qty ? <Check size={15} className="text-brass" /> : null}
            </button>
          );
        })}
      </div>
      {error ? <p className="text-danger text-sm" data-testid="upsell-error">{error}</p> : null}
      <button className="btn-primary w-full sm:w-auto inline-flex items-center justify-center gap-2"
              disabled={!chosen.length || busy} onClick={pay} data-testid="upsell-pay">
        {busy ? <Loader2 size={14} className="animate-spin" /> : null}
        {t(locale, "addExtrasCta")}{chosen.length ? ` · ${total.toFixed(2)} ${data.currency}` : ""}
      </button>
      <p className="text-xs text-muted">{t(locale, "addExtrasNote")}</p>
    </section>
  );
};

// Shown when the buyer comes back from the extras checkout.
export const ExtrasReturn = ({ locale, sessionId }) => {
  const [state, setState] = useState(null);
  const [tries, setTries] = useState(0);
  useEffect(() => {
    if (!sessionId || tries > 10 || state?.status === "paid") return undefined;
    const id = setTimeout(() => {
      api.get(`/orders/extras/status/${sessionId}`)
        .then((r) => { setState(r.data); if (r.data.status !== "paid") setTries((v) => v + 1); })
        .catch(() => setTries((v) => v + 1));
    }, tries === 0 ? 300 : 2000);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, tries, state?.status]);

  if (!sessionId) return null;
  return (
    <section className="card p-6 space-y-2" data-testid="extras-return">
      <h2 className="font-display text-xl text-ink">
        {state?.status === "paid" ? t(locale, "extrasAdded") : t(locale, "extrasPending")}
      </h2>
      {state?.status === "paid" ? (
        <p className="text-sm text-muted" data-testid="extras-return-list">
          {state.items.map((i) => `${i.quantity} × ${i.name}`).join(" · ")} · {state.amount} {state.currency} ·{" "}
          {state.order_number}
        </p>
      ) : <p className="text-sm text-muted">…</p>}
    </section>
  );
};
